/**
 * Profile Reducer
 * 
 * Manages state for the user profile including gamification data,
 * achievements, and UI preferences.
 */

import type { ProfileState, ProfileAction } from "@/types/state";

/**
 * Initial profile state
 */
export const initialProfileState: ProfileState = {
  // Data
  gamificationData: null,
  achievements: [],
  topAchievements: [],
  
  // Loading
  loading: true,
  
  // UI State
  activeTab: 'overview',
};

/**
 * Profile reducer
 * Handles all state updates for the user profile
 */
export function profileReducer(
  state: ProfileState,
  action: ProfileAction
): ProfileState {
  switch (action.type) {
    case 'SET_GAMIFICATION_DATA':
      return { ...state, gamificationData: action.payload };

    case 'SET_ACHIEVEMENTS': {
      // When setting achievements, also update top achievements
      const achievements = action.payload;
      
      // Get top 6 achievements for showcase (highest tier first)
      const tierOrder = { diamond: 5, platinum: 4, gold: 3, silver: 2, bronze: 1 };
      const sortedByTier = [...achievements].sort(
        (a, b) =>
          (tierOrder[b.tier as keyof typeof tierOrder] || 0) -
          (tierOrder[a.tier as keyof typeof tierOrder] || 0)
      );
      const topAchievements = sortedByTier.slice(0, 6);
      
      return { 
        ...state, 
        achievements,
        topAchievements,
      };
    }

    case 'SET_TOP_ACHIEVEMENTS':
      return { ...state, topAchievements: action.payload };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };

    case 'RESET_PROFILE':
      return initialProfileState;

    default:
      return state;
  }
}

/**
 * Action creators for cleaner dispatch calls
 */
export const profileActions = {
  setGamificationData: (data: ProfileState['gamificationData']): ProfileAction => ({
    type: 'SET_GAMIFICATION_DATA',
    payload: data,
  }),
  
  setAchievements: (achievements: ProfileState['achievements']): ProfileAction => ({
    type: 'SET_ACHIEVEMENTS',
    payload: achievements,
  }),
  
  setTopAchievements: (achievements: ProfileState['topAchievements']): ProfileAction => ({
    type: 'SET_TOP_ACHIEVEMENTS',
    payload: achievements,
  }),
  
  setLoading: (loading: boolean): ProfileAction => ({
    type: 'SET_LOADING',
    payload: loading,
  }),
  
  setActiveTab: (tab: ProfileState['activeTab']): ProfileAction => ({
    type: 'SET_ACTIVE_TAB',
    payload: tab,
  }),
  
  resetProfile: (): ProfileAction => ({
    type: 'RESET_PROFILE',
  }),
};